import React, { useCallback, useEffect, useState } from 'react';
import LearningContentWorksheetEditorModal from './LearningContentWorksheetEditorModal';
import {
  createDefaultLearningContentWorksheetDocument,
  type LearningContentWorksheetDocumentV1,
} from '../../types/learningContentWorksheetCanvas';
import {
  getProgramWorksheetRegisteredPages,
  replaceProgramWorksheetRegisteredPages,
} from '../../services/programWorksheetRegisteredPagesService';
import { createRegistryItemId } from '../../utils/programSlideRegisteredScopeSave';
import {
  normalizeProgramWorksheetRegistryName,
  programWorksheetRegistryFallbackName,
} from '../../utils/programWorksheetRegistryLimits';
import type { ProgramWorksheetRegisteredPage } from '../../utils/programWorksheetRegisteredPages';

const LearningContentWorksheetTemplateRegistryModal: React.FC<{
  open: boolean;
  workspaceId: string;
  onClose: () => void;
}> = ({ open, workspaceId, onClose }) => {
  const [initialDocument, setInitialDocument] = useState<LearningContentWorksheetDocumentV1 | null>(null);
  const [initialPageNames, setInitialPageNames] = useState<string[]>([]);
  const [initialPageIds, setInitialPageIds] = useState<string[]>([]);
  const [bootKey, setBootKey] = useState(0);

  useEffect(() => {
    if (!open) {
      setInitialDocument(null);
      setInitialPageNames([]);
      setInitialPageIds([]);
      return;
    }
    const sid = workspaceId.trim();
    if (!sid) return;
    let cancelled = false;
    void getProgramWorksheetRegisteredPages(sid, { forceRefresh: true })
      .then((pages) => {
        if (cancelled) return;
        if (!pages.length) {
          const empty = createDefaultLearningContentWorksheetDocument();
          setInitialDocument(empty);
          setInitialPageNames([programWorksheetRegistryFallbackName(0)]);
          setInitialPageIds([createRegistryItemId()]);
        } else {
          const doc = createDefaultLearningContentWorksheetDocument();
          const nextPages = pages.map((p) => p.layout);
          setInitialDocument({
            ...doc,
            pages: nextPages,
            layout: nextPages[0] ?? doc.layout,
          });
          setInitialPageNames(
            pages.map((p, idx) =>
              normalizeProgramWorksheetRegistryName(p.name, programWorksheetRegistryFallbackName(idx))
            )
          );
          setInitialPageIds(pages.map((p) => p.id || createRegistryItemId()));
        }
        setBootKey((k) => k + 1);
      })
      .catch(() => {
        if (cancelled) return;
        const empty = createDefaultLearningContentWorksheetDocument();
        setInitialDocument(empty);
        setInitialPageNames([programWorksheetRegistryFallbackName(0)]);
        setInitialPageIds([createRegistryItemId()]);
        setBootKey((k) => k + 1);
      });
    return () => {
      cancelled = true;
    };
  }, [open, workspaceId]);

  const handleSave = useCallback(
    async (
      document: LearningContentWorksheetDocumentV1,
      meta?: { pageNames: string[]; pageIds: string[] }
    ) => {
      const sid = workspaceId.trim();
      if (!sid) throw new Error('워크스페이스 정보가 없습니다.');
      const pages = document.pages ?? [];
      const names = meta?.pageNames ?? [];
      const ids = meta?.pageIds ?? [];
      const payload: ProgramWorksheetRegisteredPage[] = pages.map((layout, idx) => ({
        id: (ids[idx] ?? '').trim() || createRegistryItemId(),
        name: normalizeProgramWorksheetRegistryName(
          names[idx],
          programWorksheetRegistryFallbackName(idx)
        ),
        layout,
        order: idx,
        updatedAt: Date.now(),
      }));
      await replaceProgramWorksheetRegisteredPages(sid, payload);
    },
    [workspaceId]
  );

  if (!open || !initialDocument) return null;

  return (
    <LearningContentWorksheetEditorModal
      key={`worksheet-template-registry-${bootKey}`}
      open={open}
      variant="registry"
      workspaceId={workspaceId}
      draftStorageKey={`worksheet-template-registry:${workspaceId.trim()}`}
      initialDocument={initialDocument}
      initialPageNames={initialPageNames}
      initialPageIds={initialPageIds}
      onClose={onClose}
      onSave={handleSave}
    />
  );
};

export default LearningContentWorksheetTemplateRegistryModal;
